// Subtle CSS/SVG decoration — used sparingly so the design stays premium, not "cheap".

import type { CSSProperties } from "react";

/** A small cluster of balloons. Colors come from the surrounding theme vars. */
export function Balloons({ className = "" }: { className?: string }) {
  const balloon = (cx: number, cy: number, rx: number, ry: number, fill: string, delay: string) => (
    <g className="float-slow" style={{ animationDelay: delay, transformOrigin: `${cx}px ${cy}px` }}>
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={fill} />
      <path d={`M${cx} ${cy + ry} l-3 7 h6 z`} fill={fill} opacity={0.85} />
      <path d={`M${cx} ${cy + ry + 6} q6 14 0 28`} stroke="currentColor" strokeWidth="1.2" fill="none" opacity={0.35} />
    </g>
  );
  return (
    <svg viewBox="0 0 160 200" className={className} aria-hidden="true">
      {balloon(54, 60, 30, 36, "var(--theme-main)", "0s")}
      {balloon(108, 50, 26, 32, "var(--theme-soft)", "1.2s")}
      {balloon(86, 96, 22, 27, "var(--theme-deep)", "0.6s")}
    </svg>
  );
}

/** Scattered confetti dots for section backgrounds (very low contrast). */
export function ConfettiDots({ className = "" }: { className?: string }) {
  const dots = [
    [12, 20, 3], [30, 70, 2], [54, 30, 2.5], [78, 64, 2], [90, 24, 3],
    [22, 110, 2], [64, 120, 2.5], [88, 104, 2], [16, 150, 2.5], [70, 168, 2],
    [40, 188, 2], [94, 150, 3],
  ];
  return (
    <svg viewBox="0 0 100 200" className={className} aria-hidden="true" preserveAspectRatio="none">
      {dots.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} fill="var(--theme-main)" opacity={0.18} />
      ))}
    </svg>
  );
}

// Ambient balloons that rise + fade behind the hero. Monotone (theme vars),
// animated with transform/opacity only, and disabled for reduced-motion users.
// A few are `hidden sm:block` so phones render fewer (perf on low-end devices).
const RISERS: {
  left: string; size: number; color: string; dur: number; delay: number; drift: string; peak: number; smOnly?: boolean;
}[] = [
  { left: "8%",  size: 26, color: "var(--theme-main)", dur: 15, delay: 0,  drift: "16px",  peak: 0.3 },
  { left: "20%", size: 16, color: "var(--theme-soft)", dur: 19, delay: 5,  drift: "-12px", peak: 0.55, smOnly: true },
  { left: "33%", size: 34, color: "var(--theme-soft)", dur: 23, delay: 9,  drift: "10px",  peak: 0.5 },
  { left: "46%", size: 20, color: "var(--theme-deep)", dur: 17, delay: 2,  drift: "-16px", peak: 0.2,  smOnly: true },
  { left: "59%", size: 28, color: "var(--theme-main)", dur: 21, delay: 12, drift: "14px",  peak: 0.28 },
  { left: "71%", size: 18, color: "var(--theme-soft)", dur: 18, delay: 6,  drift: "-10px", peak: 0.55 },
  { left: "81%", size: 38, color: "var(--theme-soft)", dur: 25, delay: 1,  drift: "12px",  peak: 0.42, smOnly: true },
  { left: "91%", size: 22, color: "var(--theme-main)", dur: 16, delay: 8,  drift: "-14px", peak: 0.26 },
];

/** Floating-balloon background. Place inside a `relative overflow-hidden` parent. */
export function FloatingBalloons({ className = "" }: { className?: string }) {
  return (
    <div className={`fb-layer ${className}`} aria-hidden="true">
      {RISERS.map((b, i) => (
        <span
          key={i}
          className={`fb${b.smOnly ? " hidden sm:block" : ""}`}
          style={{
            left: b.left,
            "--s": `${b.size}px`,
            "--c": b.color,
            "--d": `${b.dur}s`,
            "--dl": `${b.delay}s`,
            "--drift": b.drift,
            "--peak": b.peak,
          } as CSSProperties}
        />
      ))}
    </div>
  );
}
